/**
 * footer.js — one job: the footer always has a working signup.
 *
 * The Klaviyo embed is a bare <div> that Klaviyo's own script fills in. When it
 * does not fill it — the app embed is off, the form carries a
 * `previously_submitted` trigger and this visitor has already signed up on this
 * device, or the script simply has not loaded — the div stays empty, CSS
 * collapses it, and the footer ends up with no way to join the list at all.
 * That is what the owner reported missing on her phone on 2026-09-04.
 *
 * So the theme's own form is rendered underneath it, hidden, and revealed here
 * only if Klaviyo has not drawn anything by the time the page has settled. If
 * Klaviyo draws late, the observer takes the fallback straight back down, so
 * the two can never both be on screen.
 *
 * No library, no dependency, and nothing runs at all on a page with no footer
 * signup.
 */
(function () {
  var slot = document.querySelector('[data-klaviyo-slot]');
  var fallback = document.querySelector('[data-newsletter-fallback]');

  if (!slot || !fallback) return;

  /* A CHILD IS NOT A FORM (owner, 2026-09-05: "klaviyo form on footer is
     missing on product pages").

     The first version of this asked only whether Klaviyo had put a node in the
     slot. It does that BEFORE it decides whether to display anything: the
     script injects its container, then evaluates the form's triggers, and on
     this store the footer embed carries `previously_submitted`, so for anyone
     who has already signed up it injects the wrapper and then leaves it
     collapsed. The test said "Klaviyo drew", the fallback stayed hidden, and
     the footer ended up with NEITHER form — which is the page she
     photographed.

     Measuring the height instead answers the question actually being asked: is
     there a signup on this page that a customer can see? `offsetHeight` is 0
     for an empty slot, 0 for an injected-but-hidden wrapper, and non-zero the
     moment Klaviyo paints something real. It is read at most a handful of
     times, never in a loop. */
  var klaviyoDrew = function () {
    return slot.firstElementChild !== null && slot.offsetHeight > 0;
  };

  /* Klaviyo can paint after the timeout as easily as before it, and a visitor
     looking at two signup forms is worse than one that arrived a beat late. */
  var retire = function () {
    if (klaviyoDrew()) fallback.hidden = true;
  };

  if ('MutationObserver' in window) {
    /* `subtree` and `attributes` as well as `childList`, because the moment
       that matters is no longer the insertion — it is Klaviyo taking the
       `display: none` off the wrapper it inserted earlier, which is an
       attribute change on a descendant. */
    new MutationObserver(retire).observe(slot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });
  }

  /* The cheapest possible watch on "did the slot get a height". A form that
     fades in changes size without changing an attribute this observer is
     filtering for, and a fallback and a Klaviyo form on screen at once is the
     one outcome worse than neither. */
  if ('ResizeObserver' in window) {
    new ResizeObserver(retire).observe(slot);
  }

  /* NOT ON THE LOCAL PREVIEW. Klaviyo cannot set its cookie through the CLI
     proxy — it is the same mismatch that makes the popup re-fire on every page
     there — so the embed never paints on 127.0.0.1 and the fallback would fire
     every single time. The owner would then be reviewing a footer that says
     "Join the list" while the real store says "Get 15% off your first order",
     which is the opposite of what a preview is for.

     This joins the short list of preview-only differences already recorded in
     CLAUDE.md, rather than being a silent one. */
  var host = window.location.hostname;
  if (host === '127.0.0.1' || host === 'localhost') return;

  /* 2.5s is past a normal Klaviyo paint and still inside the time it takes to
     scroll a homepage to the footer. */
  window.setTimeout(function () {
    if (!klaviyoDrew()) fallback.hidden = false;
  }, 2500);
})();
